import React, { useState } from 'react';
// @ts-ignore
import '../App.css';

const METHODS = [
  { value: 'cycle',         label: 'Cycle',          desc: 'Pool mode — no supplier ID needed' },
  { value: 'targetedSnipe', label: 'Targeted Snipe',  desc: 'Surgical — no supplier ID needed' },
  { value: 'snipeLimited',  label: 'Limited Snipe',   desc: 'Pool mode — no supplier ID needed' },
  { value: 'snipe',         label: 'Legacy Snipe',    desc: 'Requires supplier ID' },
];

const RISK_LEVELS = [
  { value: 1,  label: '1 — Maximum safety (lowest speed)' },
  { value: 2,  label: '2 — Safe (default)' },
  { value: 3,  label: '3 — Low ban risk (no refunds)' },
  { value: 4,  label: '4 — Medium ban risk (no refunds)' },
  { value: 5,  label: '5 — Increased ban risk (no refunds)' },
  { value: 6,  label: '6 — Ban mode (no refunds, max speed)' },
  { value: 7,  label: '7' },
  { value: 8,  label: '8' },
  { value: 9,  label: '9' },
  { value: 10, label: '10' },
  { value: 11, label: '11' },
];

const needsThreshold = (m: string) => m === 'targetedSnipe' || m === 'cycle';

const CreateOrderPage: React.FC = () => {
  const [adminKey, setAdminKey]             = useState(sessionStorage.getItem('adminKey') || '');
  const [customerName, setCustomerName]     = useState('');
  const [amount, setAmount]                 = useState('');
  const [platform, setPlatform]             = useState('PS');
  const [transferMethod, setTransferMethod] = useState('cycle');
  const [buyNowThreshold, setBuyNowThreshold] = useState('');
  const [riskLevel, setRiskLevel]           = useState('2');
  const [supplierID, setSupplierID]         = useState('');
  const [displayName, setDisplayName]       = useState('');
  const [autoFinishCycle, setAutoFinishCycle] = useState(true);
  const [cyclePrice, setCyclePrice]         = useState('');
  const [loading, setLoading]               = useState(false);
  const [result, setResult]                 = useState<string | null>(null);
  const [lastOrder, setLastOrder]           = useState<{ name: string; amount: string; platform: string; displayName: string } | null>(null);
  const [error, setError]                   = useState<string | null>(null);
  const [copied, setCopied]                 = useState(false);

  const [cancelName, setCancelName]         = useState('');
  const [cancelLoading, setCancelLoading]   = useState(false);
  const [cancelResult, setCancelResult]     = useState<'success' | 'error' | null>(null);
  const [cancelError, setCancelError]       = useState<string | null>(null);

  const handleCreate = async () => {
    const name = customerName.trim();
    if (!name || !amount || !adminKey.trim()) return;
    if (needsThreshold(transferMethod) && !buyNowThreshold.trim()) {
      setError('Max Price per 100K is required for this method.');
      return;
    }
    if (transferMethod === 'snipe' && !supplierID.trim()) {
      setError('Supplier ID is required for Legacy Snipe.');
      return;
    }
    setLoading(true);
    setError(null);
    sessionStorage.setItem('adminKey', adminKey.trim());
    try {
      const body: Record<string, unknown> = {
        customerName: name,
        amount: Number(amount),
        platform,
        transferMethod,
        riskLevel: Number(riskLevel),
      };
      if (buyNowThreshold.trim()) body.buyNowThreshold = Number(buyNowThreshold);
      if (supplierID.trim()) body.supplierID = supplierID.trim();
      if (transferMethod === 'targetedSnipe') {
        body.autoFinishCycle = autoFinishCycle ? 1 : 0;
        if (autoFinishCycle && cyclePrice.trim()) body.cyclePrice = Number(cyclePrice);
      }

      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey.trim()}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to create order');
      else {
        setLastOrder({ name, amount, platform, displayName: displayName.trim() || name });
        setResult(data.trackingUrl);
        setCustomerName('');
        setAmount('');
        setBuyNowThreshold('');
        setDisplayName('');
        setCyclePrice('');
      }
    } catch {
      setError('Network error — check your connection');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const name = cancelName.trim();
    if (!name || !adminKey.trim()) return;
    setCancelLoading(true);
    setCancelError(null);
    setCancelResult(null);
    try {
      const res = await fetch(`/api/orders/${encodeURIComponent(name)}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminKey.trim()}`,
        },
      });
      const data = await res.json();
      if (!res.ok) setCancelError(data.error || 'Failed to cancel order');
      else { setCancelResult('success'); setCancelName(''); }
    } catch {
      setCancelError('Network error — check your connection');
    } finally {
      setCancelLoading(false);
    }
  };

  const platformLabel: Record<string, string> = { PS: 'PlayStation', XB: 'Xbox', PC: 'PC' };

  const getWelcomeMessage = (link: string) => {
    const n = lastOrder?.displayName || lastOrder?.name || '';
    const a = lastOrder?.amount || '';
    const p = platformLabel[lastOrder?.platform || 'PS'] || lastOrder?.platform || '';
    return `Hello ${n},👋

📦 Order Details:
* Amount: ${a}K coins
* Platform: ${p}

To begin processing your order, please complete your EA account details on the secure form below:

────────────────────────────────────────────────
🔗 ${link}
────────────────────────────────────────────────

Thank you for choosing OnTime 🕕

⚠️ Before submitting, please ensure the following:
   • Your account holds at least 10,000 coins
   • Transfer Market access is enabled on the EA Web App
   • You do not log in to the account again until we confirm delivery

⚠️ Please note: the daily transfer limit per account varies significantly day to day based on EA's T&Cs — typically 1–3M coins on normal days, occasionally less when EA tightens. Larger orders may take additional time as we work within these limits to keep your account secure.`;
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(getWelcomeMessage(result));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="ot-page">
      <nav className="ot-nav">
        <div className="ot-nav-brand">
          <img
            src="https://i.ibb.co/BV9cX3cX/Whats-App-Image-2026-05-31-at-11-43-18-PM.jpg"
            alt="On Time Store"
            className="ot-nav-logo"
          />
          <span className="ot-nav-name"><span>On Time</span> Store</span>
        </div>
        <span className="ot-nav-badge">Employee Panel</span>
      </nav>

      <div className="ot-content">

        {/* ── Create Order Card ─────────────────────────── */}
        <div className="ot-card">
          <div className="ot-card-header">
            <div className="ot-card-header-icon">
              <i className="fas fa-bolt"></i>
            </div>
            <div>
              <h2>New Delivery Order</h2>
              <p>Create a delivery and generate a tracking link for the customer</p>
            </div>
          </div>
          <div className="ot-card-body">
            {error && (
              <div className="ot-alert ot-alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {result ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="ot-alert ot-alert-success">
                  <i className="fas fa-check-circle"></i>
                  Order created! Copy the message below and send it to the customer:
                </div>
                <textarea
                  readOnly
                  value={getWelcomeMessage(result)}
                  style={{
                    width: '100%',
                    minHeight: 220,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: '0.88rem',
                    lineHeight: 1.65,
                    padding: '12px 14px',
                    resize: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button className="ot-btn ot-btn-primary ot-btn-block" onClick={handleCopy}>
                  <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                  {copied ? 'Copied!' : 'Copy Message'}
                </button>
                <button
                  className="ot-btn ot-btn-ghost"
                  onClick={() => { setResult(null); setError(null); setLastOrder(null); }}
                  style={{ alignSelf: 'flex-start' }}
                >
                  <i className="fas fa-plus"></i>
                  New Order
                </button>
              </div>
            ) : (
              <div className="ot-form">
                <div className="ot-field">
                  <label className="ot-label">Admin Key</label>
                  <input
                    className="ot-input"
                    type="password"
                    placeholder="Your admin key"
                    value={adminKey}
                    onChange={e => setAdminKey(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>

                <div className="ot-form-row">
                  <div className="ot-field">
                    <label className="ot-label">
                      Order ID
                      <span className="ot-label-hint">— Eldorado Order ID</span>
                    </label>
                    <input
                      className="ot-input"
                      type="text"
                      placeholder="e.g. 12345678"
                      value={customerName}
                      onChange={e => setCustomerName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    />
                  </div>
                  <div className="ot-field">
                    <label className="ot-label">
                      Customer Name
                      <span className="ot-label-hint">— for welcome message</span>
                    </label>
                    <input
                      className="ot-input"
                      type="text"
                      placeholder="e.g. Ahmed"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="ot-form-row">
                  <div className="ot-field">
                    <label className="ot-label">
                      Amount
                      <span className="ot-label-hint">in K coins</span>
                    </label>
                    <input
                      className="ot-input"
                      type="number"
                      placeholder="e.g. 500"
                      min="1"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreate()}
                    />
                  </div>
                  <div className="ot-field">
                    <label className="ot-label">Platform</label>
                    <select
                      className="ot-input"
                      value={platform}
                      onChange={e => setPlatform(e.target.value)}
                    >
                      <option value="PS">PlayStation</option>
                      <option value="XB">Xbox</option>
                      <option value="PC">PC</option>
                    </select>
                  </div>
                </div>

                <div className="ot-field">
                  <label className="ot-label">Transfer Method</label>
                  <select
                    className="ot-input"
                    value={transferMethod}
                    onChange={e => { setTransferMethod(e.target.value); setBuyNowThreshold(''); setSupplierID(''); }}
                  >
                    {METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>
                    ))}
                  </select>
                </div>

                {transferMethod === 'snipe' && (
                  <div className="ot-field">
                    <label className="ot-label">
                      Supplier ID
                      <span className="ot-label-hint">— required for Legacy Snipe</span>
                    </label>
                    <input
                      className="ot-input"
                      type="text"
                      placeholder="MD5 or SHA1 of supplier email"
                      value={supplierID}
                      onChange={e => setSupplierID(e.target.value)}
                    />
                  </div>
                )}

                {needsThreshold(transferMethod) && (
                  <div className="ot-form-row">
                    <div className="ot-field">
                      <label className="ot-label">
                        Max Price per 100K
                        <span className="ot-label-hint">— in USD (required)</span>
                      </label>
                      <input
                        className="ot-input"
                        type="number"
                        placeholder="e.g. 0.41"
                        min="0.01"
                        step="0.01"
                        value={buyNowThreshold}
                        onChange={e => setBuyNowThreshold(e.target.value)}
                      />
                    </div>
                    {transferMethod === 'targetedSnipe' && (
                      <div className="ot-field">
                        <label className="ot-label">Risk Level</label>
                        <select
                          className="ot-input"
                          value={riskLevel}
                          onChange={e => setRiskLevel(e.target.value)}
                        >
                          {RISK_LEVELS.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {transferMethod === 'targetedSnipe' && (
                  <div className="ot-field" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={autoFinishCycle}
                        onChange={e => setAutoFinishCycle(e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: 'var(--red)', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        Auto-finish via Cycle on daily limit
                      </span>
                    </label>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '6px 0 0 28px' }}>
                      When the Surgical Snipe hits the daily limit, automatically continue delivery using the Cycle method instead of stopping.
                    </p>
                    {autoFinishCycle && (
                      <div style={{ marginTop: 10, marginLeft: 28 }}>
                        <label className="ot-label" style={{ marginBottom: 4 }}>
                          Cycle Price per 100K
                          <span className="ot-label-hint">— in USD (optional — defaults to snipe price)</span>
                        </label>
                        <input
                          className="ot-input"
                          type="number"
                          placeholder="e.g. 0.45"
                          min="0.01"
                          step="0.01"
                          value={cyclePrice}
                          onChange={e => setCyclePrice(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                )}

                <button
                  className="ot-btn ot-btn-primary ot-btn-block"
                  onClick={handleCreate}
                  disabled={loading || !customerName.trim() || !amount || !adminKey.trim() ||
                    (needsThreshold(transferMethod) && !buyNowThreshold.trim()) ||
                    (transferMethod === 'snipe' && !supplierID.trim())}
                >
                  <i className={`fas ${loading ? 'fa-circle-notch fa-spin' : 'fa-bolt'}`}></i>
                  {loading ? 'Creating…' : 'Create Order & Get Link'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Cancel Order Card ─────────────────────────── */}
        <div className="ot-card">
          <div className="ot-card-header">
            <div className="ot-card-header-icon" style={{ color: '#f87171', borderColor: '#7f1d1d', background: 'rgba(239,68,68,0.08)' }}>
              <i className="fas fa-ban"></i>
            </div>
            <div>
              <h2>Cancel Order</h2>
              <p>Stop an active order and mark it as cancelled</p>
            </div>
          </div>
          <div className="ot-card-body">
            {cancelError && (
              <div className="ot-alert ot-alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {cancelError}
              </div>
            )}
            {cancelResult === 'success' && (
              <div className="ot-alert ot-alert-success">
                <i className="fas fa-check-circle"></i>
                Order cancelled. The customer will see a cancellation notice.
              </div>
            )}
            <div className="ot-ba-row">
              <input
                className="ot-ba-input"
                type="text"
                placeholder="Customer name / Order ID to cancel"
                value={cancelName}
                onChange={e => { setCancelName(e.target.value); setCancelResult(null); setCancelError(null); }}
                disabled={cancelLoading}
                onKeyDown={e => e.key === 'Enter' && handleCancel()}
              />
              <button
                className="ot-btn ot-btn-primary"
                style={{ background: '#991b1b', borderColor: '#7f1d1d', flexShrink: 0 }}
                onClick={handleCancel}
                disabled={cancelLoading || !cancelName.trim() || !adminKey.trim()}
              >
                <i className={`fas ${cancelLoading ? 'fa-circle-notch fa-spin' : 'fa-ban'}`}></i>
                {cancelLoading ? 'Cancelling…' : 'Cancel'}
              </button>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Admin key entered above is used for authentication. If running on FutTransfer, the order will be stopped automatically.
            </p>
          </div>
        </div>

        <div className="ot-footer">
          © {new Date().getFullYear()} <span>On Time Store</span> · Employee Panel
        </div>
      </div>
    </div>
  );
};

export default CreateOrderPage;
